import { Injectable } from '@angular/core';
import { ContextService } from './context.service';
import { LoggingService } from '../../services/logging.service';
import { environment } from 'src/environments/environment';
import { METADATA } from '../constants';

/**
 * AuthLoggingIntegrationService integrates authentication and asset selection
 * with the logging system to automatically enrich log context with user data.
 * 
 * This service should be called after successful authentication to capture:
 * - Cognito user information
 * - User groups/roles
 * - Selected asset information
 * - Partner and institution details
 */
@Injectable({
  providedIn: 'root'
})
export class AuthLoggingIntegrationService {
  private enableConsoleOutput: boolean;

  constructor(
    private contextService: ContextService,
    private loggingService: LoggingService
  ) {
    this.enableConsoleOutput = environment.logging?.enableConsoleOutput ?? true;
  }

  /**
   * Helper method for conditional debug logging
   */
  private logDebug(...args: any[]): void {
    if (this.enableConsoleOutput) {
      console.log(...args);
    }
  }

  /**
   * Helper method for conditional warning logging
   */
  private logWarn(...args: any[]): void {
    if (this.enableConsoleOutput) {
      console.warn(...args);
    }
  }

  /**
   * Helper method for conditional error logging
   */
  private logError(...args: any[]): void {
    if (this.enableConsoleOutput) {
      console.error(...args);
    }
  }

  /**
   * Enrich logging context with authenticated user information
   * Should be called after successful login
   * 
   * @param cognitoUser Optional Cognito user object (will fetch if not provided)
   */
  async enrichContextWithAuthUser(cognitoUser?: any): Promise<void> {
    try {
      // Signal that login is in progress to prevent auth check loops
      this.loggingService.setLoginInProgress(true);
      
      // If no user is provided, skip enrichment to avoid blocking during login
      if (!cognitoUser) {
        this.logDebug('AuthLoggingIntegration: No user provided, skipping enrichment to avoid blocking login');
        return;
      }

      // Extract user information
      const username = cognitoUser.username || cognitoUser.attributes?.['cognito:username'];
      const cognitoId = cognitoUser.attributes?.sub;
      const userGroups = cognitoUser.signInUserSession?.idToken?.payload?.['cognito:groups'] || [];
      const accountId = cognitoUser.attributes?.['custom:AccountID'];

      // Update context with user information
      this.contextService.updateUserContext({
        externalUserId: username,
        // Store additional user data that might be useful for debugging
        component: 'auth',
        action: 'user-authenticated'
      });

      // Enable CloudWatch logging completely asynchronously (fire-and-forget)
      setTimeout(() => {
        this.enableCloudWatchAfterAuth().catch(error => {
          this.logError('Failed to enable CloudWatch after auth:', error);
        });
      }, 0);

      // Log successful authentication (development only) - also async
      if (!environment.production) {
        setTimeout(() => {
          this.loggingService.info('User authenticated successfully', {
            username,
            cognitoId,
            userGroups,
            accountId,
            timestamp: new Date().toISOString()
          });
        }, 100);
      }

    } catch (error) {
      this.logError('AuthLoggingIntegration: Error enriching context with auth user', error);
    } finally {
      // Clear login in progress flag after a delay
      setTimeout(() => {
        this.loggingService.setLoginInProgress(false);
      }, 3000); // Give 3 seconds for login process to complete
    }
  }

  /**
   * Enrich logging context with selected asset information
   * Should be called when user selects an asset
   * 
   * @param assetId The selected asset ID
   * @param assetDetails Optional additional asset details (FI name, product, etc.)
   */
  async enrichContextWithAsset(
    assetId: string,
    assetDetails?: {
      financialInstitution?: string;
      product?: string;
      core?: string;
      los?: string;
      partnerName?: string;
      partnerId?: string;
      coreCompany?: string;
      externalId?: string;
    }
  ): Promise<void> {
    setTimeout(async () => {
      try {
        if (!assetId) {
          this.logWarn('AuthLoggingIntegration: No asset ID provided');
          return;
        }

        // Update context with asset information
        this.contextService.updateUserContext({
          assetId,
          institution: assetDetails?.financialInstitution,
          product: assetDetails?.product || METADATA.PRODUCT_NAME, // When the log is sent, this value will be Bridge
          partner: assetDetails?.coreCompany || assetDetails?.partnerName,
          partnerId: assetDetails?.partnerId,
          externalId: assetDetails?.externalId
        });

        if (!environment.production) {
          setTimeout(() => {
            this.loggingService.info('Asset selected', {
              assetId,
              ...assetDetails,
              timestamp: new Date().toISOString()
            });
          }, 100);
        }

      } catch (error) {
        this.logError('AuthLoggingIntegration: Error enriching context with asset', error);
      }
    }, 0);
  }

  /**
   * Clear user context on logout
   * Should be called when user logs out
   */
  clearUserContext(): void {
    try {
      this.contextService.clearUserContext();
      
      // Log logout (development only)
      if (!environment.production) {
        this.loggingService.info('User logged out - context cleared');
      }
    } catch (error) {
      this.logError('AuthLoggingIntegration: Error clearing user context', error);
    }
  }

  /**
   * Update context when user navigates to a different component
   * 
   * @param component Component name
   * @param action Optional action being performed
   */
  updateComponentContext(component: string, action?: string): void {
    try {
      this.contextService.updateComponentContext(component, action);
    } catch (error) {
      this.logError('AuthLoggingIntegration: Error updating component context', error);
    }
  }

  /**
   * Enable CloudWatch logging after user authentication
   * This ensures CloudWatch is only enabled when we have user context
   * and prevents blocking during app startup
   */
  private async enableCloudWatchAfterAuth(): Promise<void> {
    try {

      // Safely read optional config flags; default to false if missing
      const enableCloudWatchTesting =
        typeof (environment as Record<string, unknown>).enableCloudWatchTesting === 'boolean'
          ? (environment as Record<string, unknown>).enableCloudWatchTesting
          : false;
          
      const enableCloudWatchOnStartup = environment.logging?.enableCloudWatchOnStartup ?? false;
 
      // Check if we should enable CloudWatch (production, testing, or startup config)
      if (environment.production || enableCloudWatchTesting || enableCloudWatchOnStartup) {
        // Enable CloudWatch logging completely asynchronously
        this.loggingService.enableCloudWatchLoggingAsync().catch(error => {
          this.logError('CloudWatch enablement failed:', error);
        });
        
        if (!environment.production) {
          if (enableCloudWatchTesting) {
            this.logDebug('CloudWatch logging enabled after authentication (TESTING FLAG)');
            this.logDebug('Set enableCloudWatchTesting: false in environment to disable');
          } else if (enableCloudWatchOnStartup) {
            this.logDebug('CloudWatch logging enabled after authentication (STARTUP CONFIG)');
            this.logDebug('Set enableCloudWatchOnStartup: false in logging config to disable');
          }
        }
        // CloudWatch enabled silently in production
      } else {
        this.logDebug('CloudWatch logging disabled');
        this.logDebug('Enable via: enableCloudWatchTesting: true OR enableCloudWatchOnStartup: true');
      }
    } catch (error) {
      this.logError('AuthLoggingIntegration: Error enabling CloudWatch after auth', error);
    }
  }
}

